import { ValueFormatterParams } from "@ag-grid-community/core";
import { CheckSquareOutlined, DeleteOutlined, StopOutlined } from "@ant-design/icons";
import { Checkbox, Modal, Tooltip } from "antd";
import { ForwardedRef, forwardRef } from "react";
import AGProductList from "./AGProductList";
import useApi from "../../hooks/useApi";
import userConfig from "../../api/userConfigApi";
import UsernameEditor from "./AGUsernameEditor";

export const AGCheckbox = (props: ValueFormatterParams<any, any>) => {

    const hasProducts = props?.data?.productList && props?.data?.productList.length > 0;
    const isValidUser = typeof props?.data?.id === 'number';
    
    if (!isValidUser) return null;

    return (
        <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%',
            opacity: hasProducts ? 1 : 0.4
        }}>
                <Checkbox 
                    checked={props?.value ? true : false} 
                    disabled={!hasProducts}
                    style={{ 
                        pointerEvents: hasProducts ? 'auto' : 'none',
                        cursor: hasProducts ? 'pointer' : 'not-allowed'
                    }}
                />
        </div>
    );
}


export const getUserConfigDefs = ({ afterUpdate, handleProducts, getOrganization, productList, user }: any) => {
    const ActionButton = (props: ValueFormatterParams<any, any>) => {
        const deleteUserApi = useApi(userConfig.DeleteUser)

        const handleOk = async () => {
            const response = await deleteUserApi.request(`${props?.data?.username}@${getOrganization()}`)
            if (response?.data) {
                afterUpdate();
            }
        }

        const handleDeleteAction = () => {
            Modal.confirm({
                title: `Are you sure you want to delete ${props?.data?.username} ?`,
                content: "This action cannot be undone.",
                okText: "Yes",
                cancelText: "No",
                onOk: () => {
                    handleOk()
                },
            });
        };
        const handleDisableAction = (flag: boolean) => {
            const title = `Are you sure you want to ${flag ? "enable" : "disable"} ${props?.data?.username} ?`
            Modal.confirm({
                title: title,
                okText: "Yes",
                cancelText: "No",
                onOk: () => {
                    props?.data?.saveData(props, flag);
                    afterUpdate();
                },
            });

        };

        return (
            <div  >
                {props?.data?.id >= 0 && disableColumn(props) && 
                    <div className="inline-flex gap-2">
                        <Tooltip title={'Delete'}>
                            <DeleteOutlined onClick={() => handleDeleteAction()} />
                        </Tooltip>
                        <Tooltip title={props?.data?.is_active ? 'Disable' : 'Enable'}>
                            {props?.data?.is_active ?
                                <StopOutlined onClick={() => handleDisableAction(false)} /> :
                                <CheckSquareOutlined onClick={() => handleDisableAction(true)} />}
                        </Tooltip>
                    </div>
                }

            </div>
        )
    }

    const disableColumn = (params: any) => {
        return params?.data?.username + params?.data?.domain === user?.email ? false : true
    }

    const isPermissionEditable = (params: any) => {
        return params?.data?.productList && params?.data?.productList.length > 0;
    };

    // Helper function for cell styling
    const getPermissionCellStyle = (params: any) => {
        const hasProducts = params?.data?.productList && params?.data?.productList.length > 0;
        const hasUsername = params?.data?.username && params?.data?.username.trim() !== '';
                    
        // Show placeholder only if user has username but no products
        const shouldShowPlaceholder = !hasProducts && hasUsername;
        return {
            cursor: shouldShowPlaceholder ?  "not-allowed" : "pointer",
            backgroundColor: shouldShowPlaceholder ? "#f9f9f9" : "transparent",
            border: shouldShowPlaceholder ? "1px solid #e8e8e8" : "none"
        };
    };

    return (
        [
            {
                field: "username",
                headerName: "User Name",
                headerTooltip: "User Name",
                tooltipField: "username",
                cellStyle: { "cursor": "pointer" },
                cellClass: 'border-right',
                show: true,
                filter: 'agMultiColumnFilter',
                menuTabs: ['filterMenuTab', 'generalMenuTab'],
                editable: (params: any) => {
                    return typeof params?.data?.id === 'number' ? false : true
                },
                valueFormatter: (props: ValueFormatterParams) => {
                    return props?.data?.username ?
                        `${props?.data?.username}@${getOrganization()}`
                        : null;
                },
                cellEditor: UsernameEditor,
                flex: 2,
                minWidth: 250
            },
            {
                field: "first_name",
                headerName: "First Name",
                headerTooltip: "First Name",
                tooltipField: "manager",
                cellStyle: { "cursor": "pointer" },
                cellClass: 'border-right',
                show: true,
                filter: 'agMultiColumnFilter',
                menuTabs: ['filterMenuTab', 'generalMenuTab'],
                editable:true,
                width: 100
            },
            {
                field: "last_name",
                headerName: "Last Name",
                headerTooltip: "Last Name",
                tooltipField: "last_name",
                cellStyle: { "cursor": "pointer" },
                cellClass: 'border-right',
                show: true,
                filter: 'agMultiColumnFilter',
                menuTabs: ['filterMenuTab', 'generalMenuTab'],
                editable:true,
                width: 100
            },
            {
                field: "manage_users",
                headerName: "Manage Users",
                headerTooltip: "Manage Users",
                tooltipField: "manage_users",
                cellStyle: getPermissionCellStyle,
                cellClass: 'border-right',
                show: true,
                filter: 'agMultiColumnFilter',
                menuTabs: ['filterMenuTab', 'generalMenuTab'],
                cellEditor: AGCheckbox,
                cellRenderer: AGCheckbox,
                width: 120,
                editable: isPermissionEditable,
                suppressKeyboardEvent: (params: any) => {
                    // Prevent keyboard events when no products
                    return !isPermissionEditable(params);
                }
            },
            {
                field: "manage_teams",
                headerName: "Manage Teams",
                headerTooltip: "Manage Teams",
                tooltipField: "manage_teams",
                show: true,
                cellClass: 'border-right',
                cellStyle: getPermissionCellStyle,
                filter: 'agMultiColumnFilter',
                menuTabs: ['filterMenuTab', 'generalMenuTab'],
                cellEditor: AGCheckbox,
                cellRenderer: AGCheckbox,
                width: 120,
                editable: isPermissionEditable,
                suppressKeyboardEvent: (params: any) => {
                    return !isPermissionEditable(params);
                }
            },
            {
                field: "manage_performance_targets",
                headerName: "Manage Performance Targets",
                headerTooltip: "Manage Performance Targets",
                tooltipField: "manage_performance_targets",
                show: true,
                cellClass: 'border-right',
                filter: 'agMultiColumnFilter',
                menuTabs: ['filterMenuTab', 'generalMenuTab'],
                cellEditor: AGCheckbox,
                cellRenderer: AGCheckbox,
                width: 180,
                cellStyle: (params: any) => ({
                    ...getPermissionCellStyle(params),
                    whiteSpace: 'nowrap'
                }),
                editable: isPermissionEditable,
                suppressKeyboardEvent: (params: any) => {
                    return !isPermissionEditable(params);
                }
            },
            {
                field: "products",
                headerName: "Products",
                headerTooltip: "Products",
                tooltipField: "products",
                show: true,
                cellClass: 'border-right',
                filter: 'agMultiColumnFilter',
                menuTabs: ['filterMenuTab', 'generalMenuTab'],
                flex: 2,
                minWidth: 300,
                filterParams: {
                    filters: [
                        {
                            filter: 'agTextColumnFilter',
                            filterParams: {
                                textMatcher: (data: any) => {
                                    return data?.data?.productList?.toString()?.toLowerCase().includes(data.filterText.toLowerCase());
                                },
                            }
                        },
                        {
                            filter: 'agSetColumnFilter',
                            filterParams: {
                                convertValuesToStrings: true,
                                keyCreator: (params: any) => params?.data?.productList,
                            }
                        },
                    ],
                },
                cellEditor: forwardRef((params: any, ref: ForwardedRef<any>) => {
                    const stopEditing = () => {
                        handleProducts(params)
                        params.stopEditing();
                        
                        // Refresh the entire row to update permission column states
                        setTimeout(() => {
                            if (params.api && params.node) {
                                params.api.refreshCells({ 
                                    rowNodes: [params.node], 
                                    force: true 
                                });
                            }
                        }, 100);
                    };
                    const onChange = (params: any) => { }
                    return <AGProductList data={params?.data} value={params.value} stopEditing={stopEditing} onChange={onChange} ref={ref} params={params} productList={productList} />;
                }),
                cellRenderer: (params: any) => {
                    const hasProducts = params?.data?.productList && params?.data?.productList.length > 0;
                    const hasUsername = params?.data?.username && params?.data?.username.trim() !== '';
                    
                    // Show placeholder only if user has username but no products
                    const shouldShowPlaceholder = !hasProducts && hasUsername;
                    
                    return (
                        <div style={{
                            color: hasProducts ? 'inherit' : '#999',
                            fontStyle: shouldShowPlaceholder ? 'italic' : 'normal',
                            padding: '4px 0'
                        }}>
                            {hasProducts 
                                ? params?.data?.productList 
                                : shouldShowPlaceholder 
                                    ? 'Select the products that the user can view.'
                                    : ''
                            }
                        </div>
                    );
                },
                editable:true
            },
            {
                field: "action",
                headerName: "",
                editable: false,
                sortable: false,
                filter: false,
                show: true,
                maxWidth: 60,
                minWidth: 60,
                resizable: false,
                cellStyle: () => { return { cursor: "pointer" } },
                cellRenderer: ActionButton,
            },
        ]
    )
}
